import { DateTime } from 'luxon';
import { Iteration } from 'schemas/Iteration.schema.ts';
import { SummaryReport } from 'schemas/SummaryReport.schema';
import { ITERATION_STATUS } from 'schemas/CampaignApiDetails.schema.ts';

export const getIsCurrentSummaryReport = (
  report: SummaryReport,
  iteration: Iteration,
): boolean => {
  // Summary reports which predate having a run_time are for sure stale by now.
  if (!report.run_time) {
    return false;
  }
  // When updating the staleTime, ensure that you update it on the API also.
  const staleAt = DateTime.fromSQL(report.run_time)
    .plus({ days: 1 })
    .set({
      hour: 17,
      minute: 0,
      second: 0,
      millisecond: 0,
    });
  const isFresh = DateTime.now() < staleAt;
  const isCurrentVersion = iteration.Version === report.iteration_version;

  return isFresh && isCurrentVersion;
};

export enum SummaryReportActions {
  SelectReviewers = 'SelectReviewers',
  Approve = 'Approve',
  Reject = 'Reject',
  Send = 'Send',
}

export const getSummaryReportActionsForStatus = (
  isCurrentSummaryReport: boolean,
  isWriter: boolean,
  iterationStatus: ITERATION_STATUS,
): SummaryReportActions[] => {
  if (!isCurrentSummaryReport) {
    return [];
  }

  const {
    SelectReviewers,
    Approve,
    Reject,
    Send,
  } = SummaryReportActions;
  const {
    REPORT_IN_REVIEW,
    REPORT_READY,
    REPORT_APPROVED,
    REPORT_REJECTED,
  } = ITERATION_STATUS;

  switch (iterationStatus) {
    case REPORT_READY:
      return isWriter ? [SelectReviewers] : [];
    case REPORT_IN_REVIEW:
      return [Approve, Reject];
    case REPORT_APPROVED:
      return [Reject, Send];
    case REPORT_REJECTED:
      return [];
    default:
      return [];
  }
};

export enum SummaryReportActionButton {
  NO_ACTION = 'no action',
  SELECT_REVIEWERS = 'select reviewers',
  REJECT_ONLY = 'reject only',
  APPROVE_REJECT = 'approve reject',
}

export const getSummaryReportActionButtons = (report: SummaryReport, availableActions: SummaryReportActions[], isApprover: boolean): SummaryReportActionButton => {
  const showRejectButton = availableActions.includes(SummaryReportActions.Reject) && isApprover;
  const showApproveButton = availableActions.includes(SummaryReportActions.Approve) && isApprover;
  const showSelectReviewersButton = availableActions.includes(SummaryReportActions.SelectReviewers);

  if (report.dry_run) {
    return SummaryReportActionButton.NO_ACTION;
  }

  if (!showRejectButton && !showApproveButton && showSelectReviewersButton) {
    return SummaryReportActionButton.SELECT_REVIEWERS;
  }
  if (showRejectButton && !showApproveButton && !showSelectReviewersButton) {
    return SummaryReportActionButton.REJECT_ONLY;
  }
  if (showRejectButton && showApproveButton && !showSelectReviewersButton) {
    return SummaryReportActionButton.APPROVE_REJECT;
  }

  return SummaryReportActionButton.NO_ACTION;
};
